import { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { InterviewRoundTab, InterviewSession } from "../../Interface/Index";
import { JobApplication } from "../../Entities/job-application.entity";
import { JobDescription } from "../../Entities/job-description.entity";
import JobDescriptionService from "../../Services/job-description.service";
import JobApplicationService from "../../Services/job.application.service";
import InterviewService from "../../Services/interview.service";
import AgentService from "../../Services/agent/agent.service";
import { useAuth } from "../AuthContext/Index";
import ModalContext from "../ModalContext/Index";
import LoaderContext from "../LoaderContext/Index";
import { useJobApplication } from "../../Hooks/useJobApplication";
import AuthService from "../../Services/auth.service";
import useOrganization from "../../Hooks/useOrganization";

export function useDashboardLogic() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { jobDescriptionId } = useParams();
  const jobApplicationIdInQuery = searchParams.get("jobApplicationId");
  const { applications } = useJobApplication();
  const { organization } = useOrganization();

  const jobDescriptionService = useMemo(
    () => JobDescriptionService.getInstance(),
    []
  );
  const jobApplicationService = useMemo(() => new JobApplicationService(), []);
  const interviewService = useMemo(() => new InterviewService(), []);
  const agentService = useMemo(() => AgentService.getInstance(), []);

  const { isUserLoggedIn } = useAuth();
  const { setShowUploadCV } = useContext(ModalContext);
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const [enabledInterviewRoundTabs, setEnabledInterviewRoundTabs] = useState<
    InterviewRoundTab[]
  >([]);
  const [appliedJob, setAppliedJob] = useState<JobApplication | null>(null);
  const [jobDescription, setJobDescription] = useState<JobDescription | null>(
    null
  );
  const [showOnlineInterviewTab, setShowOnlineInterviewTab] =
    useState<boolean>(false);
  const [interviewSessions, setInterviewSessions] = useState<
    InterviewSession[]
  >([]);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [showResultTab, setShowResultTab] = useState<boolean>(false);
  const [isProcessingNextRound, setIsProcessingNextRound] =
    useState<boolean>(false);
  const [initComplete, setInitComplete] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!jobDescriptionId) return;
      try {
        const currentUser = AuthService.getInstance().getCurrentUser();
        const jobDescription =
          await jobDescriptionService.findJobDescriptionById(jobDescriptionId);
        const { jobPost, rounds } = jobDescription;
        const formattedInterviewRounds = rounds.map((round) => ({
          roundId: round.round._id,
          roundNumber: round.roundNo,
          roundName: round.round.title,
          isEnabled: false,
          sessionId: null,
        }));
        setEnabledInterviewRoundTabs(formattedInterviewRounds);
        setJobDescription(jobPost || null);
        if (currentUser) {
          const jobApplication =
            await jobApplicationService.getJobApplicationByJobDescriptionId(
              jobDescriptionId
            );
          if (jobApplication) {
            const interviewSessions =
              await interviewService.getAllInterviewSessionById(
                jobApplication._id
              );
            const uniqueLatestSessions: InterviewSession[] = Array.from(
              (interviewSessions || [])
                .reduce((map, session) => {
                  const roundId = session.interviewRoundId;
                  if (!roundId) return map;
                  const existing = map.get(roundId);
                  if (
                    !existing ||
                    new Date(session.createdAt) > new Date(existing.createdAt)
                  ) {
                    map.set(roundId, session);
                  }
                  return map;
                }, new Map<string, InterviewSession>())
                .values()
            );
            setInterviewSessions(uniqueLatestSessions);
            const isInterview = jobApplication.status === "interview";
            const isResultTabEnable =
              jobApplication.status !== "interview" &&
              jobApplication.status !== "applied";
            setShowResultTab(isResultTabEnable);
            setShowOnlineInterviewTab(isInterview);
            setAppliedJob(jobApplication);
          }
        } else {
          setAppliedJob(null);
          setInterviewSessions([]);
        }
      } catch (error) {
        console.error("Error fetching job data:", error);
      } finally {
        setInitComplete?.(true);
      }
    };
    fetchData();
  }, [jobDescriptionId, jobApplicationIdInQuery]);

  useEffect(() => {
    if (!initComplete) return;
    if (isProcessingNextRound) return;

    agentService.setAgentMessageBasedOnJobApplication(
      appliedJob,
      interviewSessions,
      enabledInterviewRoundTabs,
      jobApplicationIdInQuery
    );
  }, [
    appliedJob,
    interviewSessions,
    enabledInterviewRoundTabs,
    jobApplicationIdInQuery,
    agentService,
    initComplete,
    isProcessingNextRound,
  ]);

  useEffect(() => {
    if (interviewSessions.length > 0) {
      setEnabledInterviewRoundTabs((prev) =>
        prev.map((round) => {
          const session = interviewSessions.find(
            (s) => s.interviewRoundId === round.roundId
          );
          return session
            ? { ...round, isEnabled: true, sessionId: session._id }
            : round;
        })
      );
    }
  }, [interviewSessions]);

  const refetchSessionByJobApplicationId = async () => {
    if (!appliedJob) {
      console.error("No applied job found to refetch sessions");
      return;
    }
    try {
      const sessions = await interviewService.getAllInterviewSessionById(
        appliedJob._id
      );
      const uniqueLatestSessions: InterviewSession[] = Array.from(
        (sessions || [])
          .reduce((map, session) => {
            const roundId = session.interviewRoundId;
            if (!roundId) return map;
            const existing = map.get(roundId);
            if (
              !existing ||
              new Date(session.createdAt) > new Date(existing.createdAt)
            ) {
              map.set(roundId, session);
            }
            return map;
          }, new Map<string, InterviewSession>())
          .values()
      );
      setInterviewSessions(uniqueLatestSessions);
    } catch (error: any) {
      console.error("Error in refetchSessionByJobApplicationId:", error);
    }
  };

  const handleViewRound = () => {
    if (!interviewSessions || interviewSessions.length === 0) {
      console.error("No interview session found in the context");
      return;
    }
    const sortedInterviewSessions = [...interviewSessions].sort(
      (a, b) => a.interviewRoundNumber - b.interviewRoundNumber
    );
    const lastPendingSession = sortedInterviewSessions.find(
      (session) => session.status === "pending"
    );
    if (!lastPendingSession) {
      console.error("No pending interview session found");
      return;
    }
    navigate(`/${jobDescriptionId}/interview-round/${lastPendingSession._id}`);
  };

  const handleApply = async (jdId: string, appliedJobs: JobApplication[]) => {
    setLoading(true);
    setLoadingMessage("Sending application...");
    try {
      const application = await jobApplicationService.sendJobApplication(
        jdId,
        appliedJobs,
        organization?.orgName || "QualifyMe",
        jobDescription?.title || "Unknown Job Post"
      );
      if (application) {
        setAppliedJob(application);
      }
    } catch (error) {
      console.error("Error in handleApply:", error);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage(null);
      }, 1000);
    }
  };

  const handleViewQuestions = () => {
    navigate(`/questionnaire?jdId=${jobDescriptionId}`);
  };

  const handleUploadClick = () => {
    setShowUploadCV(true);
  };

  const handleSelectSlot = () => {
    navigate(`/${jobDescriptionId}/online-interview`);
  };

  const handleContactUs = () => {
    const supportEmail = organization?.supportEmail
      ? organization.supportEmail
      : "support@qualifyme.ai";
    navigate(`/${jobDescriptionId}/requirements`);
    window.open(
      `https://mail.google.com/mail/u/0/?fs=1&to=${supportEmail}&tf=cm`,
      "_blank"
    );
  };

  const onActionButtonClick = async (action: string) => {
    switch (action) {
      case "Apply Now":
        if (isUserLoggedIn) {
          if (jobDescriptionId) {
            handleApply(jobDescriptionId, applications);
          }
        } else {
          console.log("user not logged in");
          const redirectUrl = encodeURIComponent(
            `/${jobDescriptionId}/requirements`
          );
          navigate(`/sign-in?redirect_url=${redirectUrl}`);
        }
        break;
      case "Upload Resume":
        handleUploadClick();
        break;
      case "View Questionnaire":
        handleViewQuestions();
        break;
      case "View Schedule":
      case "Select Slot":
        handleSelectSlot();
        break;
      case "View Questions":
        handleViewRound();
        break;
      case "Contact Us":
        handleContactUs();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    AgentService.getInstance().subscribe(
      AgentService.EVENTS.ACTION_BUTTON_CLICKED,
      onActionButtonClick
    );
    return () => {
      AgentService.getInstance().unsubscribe(
        AgentService.EVENTS.ACTION_BUTTON_CLICKED,
        onActionButtonClick
      );
    };
  }, [interviewSessions, appliedJob, jobDescriptionId, jobDescription]);

  const enableRounds = (roundId: string, sessionId: string) => {
    setEnabledInterviewRoundTabs((prev) =>
      prev
        .map((round) => {
          if (round.roundId === roundId) {
            return { ...round, isEnabled: true, sessionId };
          }
          return round;
        })
        .sort((a, b) => a.roundNumber - b.roundNumber)
    );
  };

  const addSession = (interviewSession: InterviewSession) => {
    setInterviewSessions((prev) => [...prev, interviewSession]);
  };

  const addSessionAndEnableRound = (interviewSession: InterviewSession) => {
    setInterviewSessions((prev) => [...prev, interviewSession]);
    setEnabledInterviewRoundTabs((prev) =>
      prev.map((round) =>
        round.roundId === interviewSession.interviewRoundId
          ? { ...round, isEnabled: true, sessionId: interviewSession._id }
          : round
      )
    );
  };

  const checkLastRound = (sessionId: string): boolean => {
    const round = enabledInterviewRoundTabs.find(
      (round) => round.sessionId === sessionId
    );
    if (!round) {
      console.log("checkLastRound: round not found");
      return false;
    }
    const isLast =
      enabledInterviewRoundTabs.sort((a, b) => b.roundNumber - a.roundNumber)[0]
        .roundId === round.roundId;
    return isLast;
  };

  const updateSession = (
    sessionId: string,
    updatedSession: InterviewSession
  ) => {
    setInterviewSessions((prev) =>
      prev.map((session) =>
        session._id === sessionId ? updatedSession : session
      )
    );
  };

  return {
    enabledInterviewRoundTabs,
    setEnabledInterviewRoundTabs,
    appliedJob,
    setAppliedJob,
    jobDescription,
    setJobDescription,
    showOnlineInterviewTab,
    setShowOnlineInterviewTab,
    interviewSessions,
    setInterviewSessions,
    enableRounds,
    addSession,
    addSessionAndEnableRound,
    checkLastRound,
    selectedKey,
    setSelectedKey,
    showResultTab,
    setShowResultTab,
    refetchSessionByJobApplicationId,
    updateSession,
    isProcessingNextRound,
    setIsProcessingNextRound,
    // Expose handlers if needed elsewhere
    handleApply,
    handleViewRound,
    handleViewQuestions,
    handleUploadClick,
    handleSelectSlot,
    handleContactUs,
    onActionButtonClick,
    initComplete,
    setInitComplete,
  };
}
